/**
 * ContextStalenessBanner (FLOW-582)
 *
 * Reads the precomputed staleness flag for a work item's seeded business
 * context and asks whether the item is still relevant. The comparison ran at
 * write time in the database (migration 054) — nothing is diffed here.
 *
 * Renders nothing when the item was never seeded or its context is unchanged,
 * which is the overwhelmingly common case.
 */

import {
  Component,
  For,
  Show,
  createEffect,
  createResource,
  createSignal,
} from "solid-js";
import { Portal } from "solid-js/web";
import {
  assessContextRelevance,
  getContextStaleness,
  seedContextFingerprint,
} from "../../api/workItemContext";
import { useToast } from "../../contexts/ToastContext";
import type { RelevanceVerdict, WorkItemId } from "../../api/types";

const PROMPT = "Is this item still relevant?";
const CONFIRMED = "Marked as still relevant. The context snapshot is current.";
const SEED_FAILED = "Could not confirm relevance. Please try again.";
const ASSESSING = "Assessing whether this item is still relevant…";

const VERDICT_LABEL: Record<RelevanceVerdict, string> = {
  relevant: "Likely still relevant",
  needs_review: "Needs a closer look",
  superseded: "Likely superseded",
};

export interface ContextStalenessBannerProps {
  workItemId: WorkItemId;
}

export const ContextStalenessBanner: Component<ContextStalenessBannerProps> = (
  props,
) => {
  const toast = useToast();
  const [staleness, { mutate }] = createResource(
    () => props.workItemId,
    getContextStaleness,
  );
  const [acknowledging, setAcknowledging] = createSignal(false);
  const [confirmed, setConfirmed] = createSignal(false);
  const [seedError, setSeedError] = createSignal<string | null>(null);
  const [assessing, setAssessing] = createSignal(false);

  // Not a signal: reading it must never itself schedule a re-run of the
  // effect below (the loop D1 rules out). A plain variable persists for as
  // long as this banner stays mounted, which is the lifetime "once per" needs.
  let requestedFor: string | undefined;

  // The live region is mounted empty on first render and only *later* receives
  // text. Inserting an already-populated role="status" element is unreliably
  // announced (NVDA/JAWS commonly miss it), and it is portalled so an
  // always-present element costs the host page no layout.
  const [announcement, setAnnouncement] = createSignal("");

  let panel: HTMLDivElement | undefined;

  // Reading an errored resource throws, and the nearest ErrorBoundary is the
  // app root — a failed fetch for this optional prompt must render nothing
  // rather than blank the whole detail page.
  const drifted = () => {
    if (staleness.state !== "ready") return undefined;
    const current = staleness();
    return current?.is_stale ? current : undefined;
  };

  // Single source of truth for the announcement: derived from the resource
  // itself rather than written from two places (the initial GET and the
  // assessment's `.then()`). A stored assessment — returned on the very
  // first GET, before the effect below ever requests anything — must be
  // announced exactly like one that arrives later, so both paths read the
  // same `drift.assessment` rather than one of them special-casing "just
  // fetched".
  createEffect(() => {
    const drift = drifted();
    if (!drift) return;
    const assessment = drift.assessment;
    setAnnouncement(
      assessment
        ? `${PROMPT} ${VERDICT_LABEL[assessment.verdict]}. ${assessment.rationale}`
        : PROMPT,
    );
  });

  // Requests a verdict on demand, once per (work item, reasons) pair (D1),
  // so a response for reasons since moved on is never mistaken for current —
  // matching the backend's own check on `relevance_assessed_reasons`.
  createEffect(() => {
    const drift = drifted();
    if (!drift || drift.assessment) return;
    const key = `${props.workItemId}:${JSON.stringify(drift.reasons)}`;
    if (key === requestedFor) return;
    requestedFor = key;

    const workItemId = props.workItemId;
    setAssessing(true);
    assessContextRelevance(workItemId)
      .then((refreshed) => {
        if (workItemId !== props.workItemId) return;
        // `acknowledge()` invalidates `requestedFor` the moment it re-seeds
        // successfully. If that happened while this request was in flight,
        // `requestedFor` no longer equals the key this response was made
        // for, and writing it now would resurrect a stale fingerprint the
        // reader already dismissed.
        if (key !== requestedFor) return;
        mutate(refreshed);
      })
      .catch(() => {
        // AI enriches, never blocks (AC4): a failed assessment leaves the
        // FLOW-582 banner exactly as it was — no toast, no inline error.
      })
      .finally(() => {
        if (workItemId === props.workItemId) setAssessing(false);
      });
  });

  // Seeding re-snapshots the context and clears the flag, so it runs only on
  // an explicit click — never on mount or refetch, which would discard the
  // drift signal before the reader ever saw it.
  const acknowledge = async () => {
    // Read the id before awaiting: the detail page keeps this component
    // mounted across sibling items, and a late response must not be written
    // into a resource that has since moved to a different work item.
    const workItemId = props.workItemId;
    setAcknowledging(true);
    setSeedError(null);
    try {
      const refreshed = await seedContextFingerprint(workItemId);
      if (workItemId !== props.workItemId) return;
      // Any assessment request still in flight was made for the fingerprint
      // this reseed just replaced. Clearing the key it was keyed on makes
      // the assessment effect's `key !== requestedFor` guard drop that
      // response instead of resurrecting the dismissed banner. A future
      // drift computes a fresh key regardless, so clearing to `undefined`
      // here costs nothing.
      requestedFor = undefined;
      // setConfirmed first: mutate() on its own flips the outer <Show> falsy
      // and destroys the panel, which setConfirmed would then rebuild — a
      // pointless unmount/remount that leaves the focus call below depending
      // on the two writes landing in this order.
      setConfirmed(true);
      mutate(refreshed);
      setAnnouncement(CONFIRMED);
      // The button the reader just activated is gone; without this, focus
      // falls to <body> and the tab order restarts at the top of the page.
      panel?.focus();
    } catch {
      if (workItemId !== props.workItemId) return;
      setSeedError(SEED_FAILED);
      setAnnouncement(SEED_FAILED);
      toast.showError(SEED_FAILED);
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <>
      <Portal>
        <div
          role="status"
          aria-live="polite"
          data-testid="context-staleness-live-region"
          class="sr-only"
        >
          {announcement()}
        </div>
      </Portal>

      <Show when={drifted() || confirmed()}>
        <div
          ref={panel}
          tabindex="-1"
          data-testid="context-staleness-panel"
          class="focus:outline-none"
        >
          <Show
            when={drifted()}
            fallback={
              <p
                data-testid="context-staleness-confirmed"
                class="rounded border border-line bg-sunken p-4 text-sm text-ink-2"
              >
                {CONFIRMED}
              </p>
            }
          >
            {(drift) => (
              <div
                data-testid="context-staleness-banner"
                class="space-y-2 rounded border border-signal-amber/25 bg-signal-amber-tint p-4"
              >
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 class="text-sm font-semibold text-signal-amber">
                      {PROMPT}
                    </h3>
                    <p class="mt-1 text-xs text-ink-2">
                      The business context this item was written against has
                      changed.
                    </p>
                  </div>
                  <button
                    type="button"
                    class="shrink-0 self-start rounded border border-line bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-sunken hover:border-line-strong focus-ring disabled:opacity-50"
                    disabled={acknowledging()}
                    onClick={acknowledge}
                  >
                    Still relevant
                  </button>
                </div>

                <Show when={assessing()}>
                  <p class="text-xs italic text-ink-2">{ASSESSING}</p>
                </Show>

                <Show when={drift().assessment}>
                  {(assessment) => (
                    <div
                      data-testid="context-staleness-assessment"
                      class="rounded border border-signal-amber/25 bg-surface/60 p-2"
                    >
                      <p class="text-xs font-semibold text-signal-amber">
                        {VERDICT_LABEL[assessment().verdict]}
                      </p>
                      <p class="mt-1 text-xs text-ink-2">
                        {assessment().rationale}
                      </p>
                      <p class="mt-1 text-[10px] font-medium uppercase tracking-wide text-ink-3">
                        AI assessment
                      </p>
                    </div>
                  )}
                </Show>

                <ul class="space-y-1 text-xs text-ink-2">
                  <For each={drift().reasons}>
                    {(reason) => (
                      <li>
                        <span class="font-medium">{reason.label}</span>
                        <span> — {reason.detail}</span>
                      </li>
                    )}
                  </For>
                </ul>

                <Show when={seedError()}>
                  {(message) => (
                    <p
                      data-testid="context-staleness-error"
                      class="text-xs font-medium text-signal-red"
                    >
                      {message()}
                    </p>
                  )}
                </Show>
              </div>
            )}
          </Show>
        </div>
      </Show>
    </>
  );
};
